import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageCircle, Send, Loader2, Paperclip, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ChatPage = ({ toast }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [images, setImages] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/chat/history`);
      setMessages(r.data || []);
    } catch (e) { /* noop */ }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(',')[1];
        setImages(prev => [...prev, {
          name: file.name,
          media_type: file.type,
          data: base64,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    if (!input.trim() && images.length === 0) return;
    setSending(true);

    const userMsg = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = input;
    const currentImages = images;
    setInput('');
    setImages([]);

    try {
      const r = await axios.post(`${BACKEND_URL}/api/chat/send`, {
        message: currentInput,
        images: currentImages.map(img => ({ media_type: img.media_type, data: img.data })),
      });

      const assistantMsg = {
        role: 'assistant',
        content: r.data.response,
        timestamp: new Date().toISOString(),
        citations: r.data.citations || [],
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (e) {
      toast({ title: 'Error enviando mensaje', variant: 'destructive' });
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Card className="h-[700px] flex flex-col">
      <CardHeader className="shrink-0">
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" /> Chat con el Coordinador
        </CardTitle>
        <CardDescription>
          Escríbele lo que necesites. Puedes adjuntar capturas de Meta Ads, TikTok Ads o lo que sea.
        </CardDescription>
      </CardHeader>

      {/* Mensajes */}
      <CardContent className="flex-1 overflow-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 py-12">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Escríbele al Coordinador. Él se encarga del resto.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
              msg.role === 'user'
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-800'
            }`}>
              {msg.role === 'assistant' && (
                <p className="text-xs font-semibold text-slate-400 mb-1">Coordinador</p>
              )}
              <pre className="whitespace-pre-wrap font-sans">{msg.content}</pre>
              {msg.citations && msg.citations.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-400">Fuentes:</p>
                  {msg.citations.slice(0, 3).map((c, j) => (
                    <a key={j} href={c.url} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-500 hover:underline block truncate">
                      {c.title || c.url}
                    </a>
                  ))}
                </div>
              )}
              <p className="text-xs opacity-40 mt-1">
                {new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 p-3 rounded-lg">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </CardContent>

      {/* Input */}
      <div className="p-4 border-t bg-white shrink-0">
        {images.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {images.map((img, i) => (
              <div key={i} className="flex items-center gap-1 bg-slate-100 rounded px-2 py-1 text-xs">
                <span>{img.name}</span>
                <button onClick={() => removeImage(i)} className="text-slate-400 hover:text-red-500">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input type="file" ref={fileInputRef} onChange={handleImageUpload}
            accept="image/*" multiple className="hidden" />
          <Button variant="outline" size="icon" onClick={() => fileInputRef.current?.click()}
            title="Adjuntar imagen (capturas de Meta Ads, TikTok Ads, etc.)">
            <Paperclip className="h-4 w-4" />
          </Button>
          <textarea
            className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            rows={2}
            placeholder="Escribe al Coordinador... (Enter para enviar, Shift+Enter para nueva línea)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button onClick={sendMessage} disabled={sending || (!input.trim() && images.length === 0)}
            className="bg-slate-900 hover:bg-slate-800">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ChatPage;
